import { useState, useRef, useCallback, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';

import DropdownMenu from '../../dropdown-menu/DropdownMenu';
import MediaDetailModal from './MediaDetailModal';
import MediaTranscriptionModal from './MediaTranscriptionModal';
import GenerativeAudioThumbnail from './audio-thumbnail/GenerativeAudioThumbnail';
import { AudioPreviewProps, MediaPreviewHandle } from '../../../types/media-preview';
import { selectIsAudioEnabled, setAudioEnabled } from '../../../app/actions/audio/audioSlice';
import { claimPlayback, releasePlayback } from '../../audio-preview/core/playbackArbiter';
import { useActiveTrackPlayback } from '../../audio-preview/core/activeTrack';
import { downloadMediaFile } from '../../../utils/downloadFile';
import { buildAudioMenuItems } from '../../../utils/audioMenu';

/**
 * オーディオのサムネイル表示（波形なし・ブラウジング/カード向け）。
 *
 * 正方形サムネイルの内容は 2 段階でフォールバックする:
 * 1. thumbnailSrc があればジャケット画像
 * 2. 無ければ生成サムネイル（グラスモーフィズム + メッシュグラデーション）。
 *    基本色は音源そのものの実測から決まる（GenerativeAudioThumbnail 参照）
 *
 * 音源はホバーで試聴しない（動画とは扱いが違う）。クリックで画面下の
 * フローティングプレイヤーバーが鳴り、サムネイルはその再生位置に合わせて
 * 動く。バーの再生は activeTrack 経由で受け取る。
 *
 * 波形付きプレビューは用途別コンポーネントへ移行済み:
 * - 編集用途（3行レイアウト・ステレオ波形）: audio-preview/PrecisionAudioPlayer
 * - Messenger 等の軽量表示: audio-preview/CommunicationAudioPlayer
 * 振り分けは MediaPreview（ファサード）が行う。
 */

// =============================================================================
// Styled Components
// =============================================================================

const AudioContainer = styled.div<{
  $mode: 'fixed-width' | 'fixed-height' | 'auto' | 'fill';
  $width?: string;
  $height?: string;
  $maxWidth?: string;
  $maxHeight?: string;
}>`
  position: relative;
  overflow: hidden;

  ${props => {
    switch (props.$mode) {
      case 'fixed-width':
        return `
          width: ${props.$width || '100%'};
          ${props.$maxHeight ? `max-height: ${props.$maxHeight};` : ''}
          aspect-ratio: 1;
        `;
      case 'fixed-height':
        return `
          height: ${props.$height || 'auto'};
          ${props.$maxWidth ? `max-width: ${props.$maxWidth};` : ''}
          aspect-ratio: 1;
        `;
      case 'fill':
        return `
          width: 100%;
          max-height: 100%;
        `;
      case 'auto':
      default:
        return `
          width: 100%;
          ${props.$maxWidth ? `max-width: ${props.$maxWidth};` : ''}
          ${props.$maxHeight ? `max-height: ${props.$maxHeight};` : ''}
          aspect-ratio: 1;
        `;
    }
  }}
`;

const ThumbnailView = styled.div<{
  $mode: 'fixed-width' | 'fixed-height' | 'auto' | 'fill';
  $height?: string;
  $width?: string;
  $hasThumbnail?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: ${props => (props.$hasThumbnail ? '#000000' : '#ffffff')};
  border: 1px solid var(--soremo-border);
  border-radius: 6px;

  .material-symbols-rounded {
    font-size: ${props => {
        switch (props.$mode) {
            case 'fixed-width':
                if (props.$width) {
                    const widthPx = parseFloat(props.$width);
                    return !isNaN(widthPx) ? `${widthPx / 2}px` : '128px';
                }
                return '128px';
            case 'fixed-height':
                if (props.$height) {
                    const heightPx = parseFloat(props.$height);
                    return !isNaN(heightPx) ? `${heightPx / 2}px` : '128px';
                }
                return '128px';
            case 'auto':
            default:
                return '128px';
        }
    }};
    color: var(--soremo-light-gray);
  }
`;

const ThumbnailImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  pointer-events: none;
`;

// =============================================================================
// メインコンポーネント
// =============================================================================

const AudioPreview = forwardRef<MediaPreviewHandle, AudioPreviewProps>(({
    src,
    thumbnailSrc = null,
    showMenu = false,
    sizeMode = { mode: 'auto', objectFit: 'contain' },
    className = '',
    style = {},
    onError = null,
    onLoaded = null,
    onPlayStateChange,
    onDownload,
    downloadUrl,
    fileName,
    fileId,
    acrScanStatus,
    acrScanResult,
    acrScannedAt,
    waveformUrl = null,
    glowTrigger = 'both',
}, ref) => {
    // --- Props から値を抽出 ---
    const mode = sizeMode.mode;
    const width = sizeMode.mode === 'fixed-width' ? sizeMode.width : undefined;
    const height = sizeMode.mode === 'fixed-height' ? sizeMode.height : undefined;
    const maxWidth = sizeMode.maxWidth;
    const maxHeight = sizeMode.maxHeight;

    // --- State ---
    const [isPlaying, setIsPlaying] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
    const [thumbnailLoadFailed, setThumbnailLoadFailed] = useState(false);

    // グローバルオーディオ状態を取得
    // オーディオ再生開始時は自動で isAudioEnabled=true にする（無音再生は意味がないため）
    const dispatch = useDispatch();
    const isAudioEnabled = useSelector(selectIsAudioEnabled);
    const effectiveMuted = !isAudioEnabled;

    // --- Refs ---
    const audioRef = useRef<HTMLAudioElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const wasPlayingBeforeHiddenRef = useRef(false);
    const hasCalledOnLoadedRef = useRef(false);
    const onLoadedRef = useRef(onLoaded);
    const onPlayStateChangeRef = useRef(onPlayStateChange);
    const previousSrcRef = useRef(src);
    const playbackTokenRef = useRef<symbol | null>(null);
    if (!playbackTokenRef.current) playbackTokenRef.current = Symbol('audio-thumbnail');

    // --- Ref同期用Effect ---
    useEffect(() => {
        onLoadedRef.current = onLoaded;
    }, [onLoaded]);

    useEffect(() => {
        onPlayStateChangeRef.current = onPlayStateChange;
    }, [onPlayStateChange]);

    useEffect(() => {
        setThumbnailLoadFailed(false);
    }, [thumbnailSrc]);

    useEffect(() => {
        if (previousSrcRef.current === src) return;

        previousSrcRef.current = src;
        setIsPlaying(false);

        const audio = audioRef.current;
        if (audio) {
            audio.pause();
            audio.currentTime = 0;
        }

        onPlayStateChangeRef.current?.(false);
    }, [src]);

    // --- 生成サムネイル（ジャケット画像が無い場合） ---
    const shouldShowThumbnail = Boolean(thumbnailSrc && !thumbnailLoadFailed);

    // フローティングプレイヤーバーで同じ音源が鳴っているか。鳴っていれば
    // サムネイルはバーの再生位置に合わせて動く（画面内に居る限り止めない）。
    // readSpectrum はバー再生中のみ実スペクトルを返し、それ以外は null →
    // peaks 駆動へフォールバック（バーと同じ動きに揃えるための配線）。
    const {
        isPlaying: isBarPlaying,
        getCurrentTime: getBarTime,
        readSpectrum,
    } = useActiveTrackPlayback(src);
    const isPlayingAnywhere = isPlaying || isBarPlaying;

    const getCurrentTime = useCallback(
        () => (isBarPlaying ? getBarTime() : audioRef.current?.currentTime ?? 0),
        [isBarPlaying, getBarTime],
    );

    // --- ダウンロード / 詳細 ---
    const handleDownloadClick = useCallback(() => {
        if (!downloadMediaFile({ downloadUrl, src, fileName, fallbackName: 'audio' })) return;
        onDownload?.();
    }, [src, downloadUrl, onDownload, fileName]);

    const handleDetailClick = useCallback(() => {
        setShowDetailModal(true);
    }, []);

    const handleTranscribeClick = useCallback(() => {
        setShowTranscriptionModal(true);
    }, []);

    const menuItems = useMemo(() => buildAudioMenuItems({
        onDetailInfo: handleDetailClick,
        onDownload: handleDownloadClick,
        onTranscribe: fileId ? handleTranscribeClick : undefined,
        acrScanStatus,
    }), [handleDetailClick, handleDownloadClick, handleTranscribeClick, fileId, acrScanStatus]);

    // --- エラーハンドラー ---
    const handleError = useCallback((error: Error | string, context = '') => {
        const errorMessage = `AudioPreview Error${context ? ` (${context})` : ''}: ${typeof error === 'string' ? error : error.message}`;
        if (onError) {
            onError(errorMessage);
        }
    }, [onError]);

    // --- 再生制御 ---
    const enableAudioAndUnmute = useCallback(() => {
        dispatch(setAudioEnabled(true));
        if (audioRef.current) {
            audioRef.current.muted = false;
        }
    }, [dispatch]);

    // --- 外部API公開 ---
    useImperativeHandle(ref, () => ({
        play: async () => {
            enableAudioAndUnmute();
            if (audioRef.current) {
                await audioRef.current.play();
            }
        },
        pause: () => {
            audioRef.current?.pause();
        },
        seek: (seconds: number) => {
            if (audioRef.current) {
                audioRef.current.currentTime += seconds;
            }
        },
        seekTo: (seconds: number) => {
            if (audioRef.current) {
                const duration = Number.isFinite(audioRef.current.duration) ? audioRef.current.duration : Infinity;
                audioRef.current.currentTime = Math.max(0, Math.min(duration, seconds));
            }
        },
        getCurrentTime: () => {
            return audioRef.current?.currentTime ?? 0;
        },
        getDuration: () => {
            return audioRef.current?.duration ?? 0;
        },
        clickToggle: () => {
            const audio = audioRef.current;
            if (!audio) return;
            if (!audio.paused) {
                audio.pause();
            } else {
                enableAudioAndUnmute();
                audio.play().catch(() => {});
            }
        },
        getMediaElement: () => audioRef.current,
    }));

    // --- ホバーハンドラー ---
    // 音源はホバーで鳴らさない。ホバー状態は生成サムネイルの光沢（グレア）に
    // 使うだけなので、状態の追跡だけを行う。
    const handleMouseEnter = useCallback(() => {
        setIsHovering(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsHovering(false);
    }, []);

    // アンマウント時・Activity 非表示時にオーディオを停止する。
    // <audio> は DOM に残ったまま（Activity hidden）や detach 後も再生が継続するため、
    // ここで明示的に pause しないとページ遷移・タブ切替後に音が残り、
    // 次のホバー再生と二重再生されて音割れの原因になる。
    // ref はクリーンアップ時に detach 済みの可能性があるため effect 本体で要素を捕捉する。
    useEffect(() => {
        const audio = audioRef.current;
        const token = playbackTokenRef.current as symbol;
        return () => {
            if (audio && !audio.paused) {
                audio.pause();
                audio.currentTime = 0;
            }
            releasePlayback(token);
        };
    }, []);

    // 画面外に出たら一時停止、画面内に戻ったら再開（OSページ準拠）
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const audio = audioRef.current;
                    if (entry.intersectionRatio < 0.1) {
                        if (audio && !audio.paused) {
                            wasPlayingBeforeHiddenRef.current = true;
                            audio.pause();
                        }
                    } else if (wasPlayingBeforeHiddenRef.current) {
                        wasPlayingBeforeHiddenRef.current = false;
                        audio?.play().catch(() => {});
                    }
                });
            },
            { threshold: [0, 0.1] },
        );

        observer.observe(container);
        return () => observer.disconnect();
    }, []);

    // ユーザーがグローバルトグルを手動OFFした場合にmutedを同期
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.muted = effectiveMuted;
        }
    }, [effectiveMuted]);

    // --- audio 要素のイベント配線（再生状態・ロード通知・排他再生） ---
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const notifyLoaded = () => {
            if (onLoadedRef.current && !hasCalledOnLoadedRef.current) {
                hasCalledOnLoadedRef.current = true;
                onLoadedRef.current();
            }
        };
        const handlePlay = () => {
            // アプリ内の同時再生を防ぐ（他プレイヤーを停止）
            claimPlayback(playbackTokenRef.current as symbol, () => audio.pause());
            setIsPlaying(true);
            onPlayStateChangeRef.current?.(true);
        };
        const handleStop = () => {
            setIsPlaying(false);
            onPlayStateChangeRef.current?.(false);
        };
        const handleAudioError = () => {
            handleError('Audio playback error', 'audio');
        };

        audio.addEventListener('loadedmetadata', notifyLoaded);
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handleStop);
        audio.addEventListener('ended', handleStop);
        audio.addEventListener('error', handleAudioError);

        return () => {
            audio.removeEventListener('loadedmetadata', notifyLoaded);
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handleStop);
            audio.removeEventListener('ended', handleStop);
            audio.removeEventListener('error', handleAudioError);
        };
    }, [src, handleError]);

    // ==========================================================================
    // レンダリング
    // ==========================================================================

    return (
        <>
            <AudioContainer
                ref={containerRef}
                className={className}
                style={style}
                $mode={mode}
                $width={width}
                $height={height}
                $maxWidth={maxWidth}
                $maxHeight={maxHeight}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <ThumbnailView
                    $mode={mode}
                    $height={height}
                    $width={width}
                    $hasThumbnail={shouldShowThumbnail}
                >
                    {shouldShowThumbnail ? (
                        <ThumbnailImage
                            src={thumbnailSrc ?? undefined}
                            alt=""
                            loading="lazy"
                            onError={() => setThumbnailLoadFailed(true)}
                        />
                    ) : (
                        <GenerativeAudioThumbnail
                            src={src}
                            waveformUrl={waveformUrl}
                            cacheKey={fileId ?? waveformUrl ?? src}
                            isPlaying={isPlayingAnywhere}
                            isHovering={isHovering}
                            glowTrigger={glowTrigger}
                            getCurrentTime={getCurrentTime}
                            readSpectrum={readSpectrum}
                            deferPeaks
                        />
                    )}
                </ThumbnailView>
                {showMenu && (
                    <DropdownMenu items={menuItems} />
                )}
                {/* preload="none" は必須。ホバー再生を廃止した今この要素は命令的な
                    play() のためだけに在るので、先読みは帯域の無駄でしかない。
                    加えて、ここで CORS なしに取得してしまうとそのキャッシュエントリが
                    残り、フローティングバーが同じ URL を crossOrigin 付きで読もうと
                    したときに弾かれる（周波数解析が使えなくなる）。 */}
                <audio ref={audioRef} src={src} preload="none" muted={effectiveMuted} />
            </AudioContainer>
            {showDetailModal && (
                <MediaDetailModal
                    onClose={() => setShowDetailModal(false)}
                    fileName={fileName}
                    acrScanResult={acrScanResult}
                    fileId={fileId}
                    acrScanStatus={acrScanStatus}
                    acrScannedAt={acrScannedAt}
                />
            )}
            {showTranscriptionModal && fileId && (
                <MediaTranscriptionModal
                    fileId={fileId}
                    fileName={fileName}
                    onClose={() => setShowTranscriptionModal(false)}
                    onSeek={(seconds) => {
                        const audio = audioRef.current;
                        if (!audio) return;
                        const duration = Number.isFinite(audio.duration) ? audio.duration : Infinity;
                        audio.currentTime = Math.max(0, Math.min(duration, seconds));
                    }}
                />
            )}
        </>
    );
});

AudioPreview.displayName = 'AudioPreview';

export default AudioPreview;
